<template>
  <div class="user-list-container">
    <div class="user-header">
      <h1>User Management</h1>
      <button class="btn-add-user" @click="openAddUserModal" title="Add New User">
        <i class="fas fa-plus"></i> Add New User
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <p>Loading users...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="fetchUsers" class="btn-retry">Retry</button>
    </div>

    <!-- Users Table -->
    <div v-else class="table-container">
      <div class="datatable-header">
        <span class="v-input-icon-left">
          <i class="pi pi-search"></i>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Search user..." 
            class="search-field"
          />
        </span>
      </div>
      <v-data-table
        :headers="headers"
        :items="filteredUsers"
        :search="searchQuery"
        :items-per-page="itemsPerPage"
        :sort-by="[{ key: sortBy, order: sortDesc ? 'desc' : 'asc' }]"
        class="elevation-0 users-datatable"
        item-key="user_id"
        hover
      >
        <template #[`item.roleid`]="{ item }">
          <span>{{ getRoleDescription(item.roleid) }}</span>
        </template>

        <template #[`item.isactive`]="{ item }">
          <span :class="item.isactive===0 ? 'status-active' : 'status-inactive'">
            {{ item.isactive===0 ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="actions-cell">
            <button @click="editUser(item)" class="btn-edit" title="Edit">
              <i class="fas fa-edit"></i>
            </button>
          </div>
        </template>

        <template #no-data>
          <div class="no-data">No users found</div>
        </template>
      </v-data-table>
    </div>

    <!-- Add/Edit User Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ isEditMode ? 'Edit User' : 'Add New User' }}</h2>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>

        <form @submit.prevent="saveUser" class="modal-form">
          <div class="form-group">
            <label for="name">Name *</label>
            <input 
              id="name"
              v-model="formData.name" 
              type="text" 
              placeholder="Enter full name"
              required
              class="form-input"
            >
            <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
          </div>

          <div class="form-group">
            <label for="username">Username *</label>
            <input 
              id="username"
              v-model="formData.username" 
              type="text" 
              placeholder="Enter username"
              required
              class="form-input"
            >
            <span v-if="errors.username" class="error-text">{{ errors.username }}</span>
          </div>

          <div class="form-group">
            <label for="password">Password *</label>
            <input 
              id="password"
              v-model="formData.password" 
              type="password" 
              placeholder="Enter password"
              :required="!isEditMode"
              class="form-input"
            >
            <span v-if="errors.password" class="error-text">{{ errors.password }}</span>
            <small v-if="isEditMode" class="help-text">Leave blank to keep current password</small>
          </div>

          <div class="form-group">
            <label for="roleid">Role *</label>
            <select 
              id="roleid"
              v-model="formData.roleid" 
              class="form-input"
              required
            >
              <option :value="null">Select a role</option>
              <option 
                v-for="role in rolesList" 
                :key="role.roleid" 
                :value="role.roleid"
              >
                {{ role.role_description }}
              </option>
            </select>
            <span v-if="errors.roleid" class="error-text">{{ errors.roleid }}</span>
          </div>

          <div class="form-group checkbox-group">
            <label>
              <input 
                v-model="formData.isactive" 
                type="checkbox"
              >
              Active
            </label>
          </div>

          <div class="modal-footer">
            <button type="button" @click="closeModal" class="btn-cancel">Cancel</button>
            <button type="submit" class="btn-save" :disabled="isSubmitting">
              <span v-if="isSubmitting">
                <i class="fas fa-spinner fa-spin"></i> Saving...
              </span>
              <span v-else>Save User</span>
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'UserList',
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      users: [],
      filteredUsers: [],
      rolesList: [],
      searchQuery: '',
      isLoading: false,
      isSubmitting: false,
      error: null,
      showModal: false,
      isEditMode: false,
      sortBy: 'name',
      sortDesc: false,
      itemsPerPage: 10,
      formData: {
        name: '',
        username: '',
        password: '',
        roleid: null,
        isactive: true,
      },
      errors: {},
      headers: [
        { title: 'Name', key: 'name', sortable: true },
        { title: 'Username', key: 'username', sortable: true },
        { title: 'Role', key: 'roleid', sortable: true },
        { title: 'Status', key: 'isactive', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false },
      ],
    };
  },
  watch: {
    searchQuery() {
      this.filterUsers();
    },
  },
  mounted() {
    this.fetchRoles();
    this.fetchUsers();
  },
  methods: {
    async fetchRoles() {
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };

        const response = await axios.get('/usertype', config);
        //console.log('Roles fetched:', response.data);

        // Handle different response formats
        if (Array.isArray(response.data)) {
          if (response.data[0]?.roleid !== undefined) {
            this.rolesList = response.data;
          } else if (response.data[0] === 1 && response.data[2]) {
            this.rolesList = response.data[2];
          }
        }
        //console.log('Roles list:', this.rolesList);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    },

    async fetchUsers() {
      this.isLoading = true;
      this.error = null;
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };

        const response = await axios.get('/userslist', config);
        //console.log('Users fetched:', response.data);

        // Handle different response formats
        let userList = response.data[2];
        if (!Array.isArray(userList)) {
          userList = response.data;
        }
        this.users = userList;
        this.filterUsers();
      } catch (error) {
        console.error('Error fetching users:', error);
        this.error = 'Failed to load users. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    filterUsers() {
      if (!this.searchQuery) {
        this.filteredUsers = [...this.users];
        return;
      }

      const query = this.searchQuery.toLowerCase();
      this.filteredUsers = this.users.filter(user =>
        user.name.toLowerCase().includes(query) ||
        user.username.toLowerCase().includes(query)
      );
    },

    getRoleDescription(roleId) {
      const role = this.rolesList.find(r => r.roleid === roleId);
      return role ? role.role_description : 'Unknown';
    },

    openAddUserModal() {
      this.isEditMode = false;
      this.formData = {
        name: '',
        username: '',
        password: '',
        roleid: null,
        isactive: true,
      };
      this.errors = {};
      this.showModal = true;
    },

    editUser(user) {
      this.isEditMode = true;
      this.formData = {
        user_id: user.user_id || user.userid, // Support both field names
        name: user.name,
        username: user.username,
        password: '', // Don't pre-fill password for security
        roleid: user.roleid,
        isactive: user.isactive === 0 || user.isactive === true,
      };
      //console.log('Editing user:', user); // Debug log
      //console.log('Form data:', this.formData); // Debug log
      this.errors = {};
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.isEditMode = false;
      this.formData = {
        name: '',
        username: '',
        password: '',
        roleid: null,
        isactive: true,
      };
      this.errors = {};
    },

    validateForm() {
      this.errors = {};
      
      if (!this.formData.name.trim()) {
        this.errors.name = 'Name is required';
      }
      
      if (!this.formData.username.trim()) {
        this.errors.username = 'Username is required';
      }
      
      if (!this.isEditMode && !this.formData.password.trim()) {
        this.errors.password = 'Password is required';
      }

      if (!this.formData.roleid) {
        this.errors.roleid = 'Role is required';
      }

      return Object.keys(this.errors).length === 0;
    },

    async saveUser() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };
        
        // Prepare data - convert boolean isactive to 0 or 1
        const userData = {
          name: this.formData.name,
          username: this.formData.username,
          roleid: this.formData.roleid,
          isactive: this.formData.isactive ? 0 : 1,
        };

        // Only include password if provided
        //if (this.formData.password) {
        userData.password = this.formData.password;
        //}

        // Include user_id for edit mode
        if (this.isEditMode) {
          userData.user_id = this.formData.user_id;
        }
        //console.log('User data to save:', userData);
        const dataform = JSON.stringify(userData);
        //console.log('Form data string:', dataform);
        const data = new FormData();
        data.append('transaction', dataform);

        if (this.isEditMode) {
          // Update existing user
          const userId = this.formData.user_id;
          //console.log('Updating user with ID:', userId);
          if (!userId) {
            throw new Error('User ID is missing. Cannot update user.');
          }
          await axios.put(`/update_user/${userId}`,data,config);
        } else {
          // Create new user
          await axios.post('/createuser', data, config);
        }

        //console.log('User saved successfully:', response.data);
        
        // Show success message
        this.toast.success(this.isEditMode ? 'User updated successfully!' : 'User created successfully!');

        // Refresh user list
        await this.fetchUsers();
        this.closeModal();
      } catch (error) {
        console.error('Error saving user:', error);
        const errorMsg = error.response?.data?.message || 'Failed to save user. Please try again.';
        
        // Show error message
        this.toast.error(errorMsg);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.user-list-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-header h1 {
  margin: 0;
  font-size: 28px;
  color: #333;
}

.btn-add-user {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s ease;
}

.btn-add-user:hover {
  background-color: #45a049;
}

.btn-add-user:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.loading-state,
.error-state {
  background: white;
  padding: 40px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-state {
  color: #d32f2f;
}

.btn-retry {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 15px;
}

.btn-retry:hover {
  background-color: #45a049;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  padding: 20px;
}

.datatable-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.v-input-icon-left {
  position: relative;
  display: inline-block;
  width: 100%;
}

.v-input-icon-left .pi {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
  z-index: 1;
}

.search-field {
  width: 250px;
  padding: 8px 10px 8px 32px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  position: relative;
  z-index: 0;
}

.search-field:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.no-data {
  text-align: center;
  color: #999;
  padding: 20px;
  font-style: italic;
}

.status-active {
  color: #4CAF50;
  font-weight: 600;
}

.status-inactive {
  color: #f44336;
  font-weight: 600;
}

.actions-cell {
  display: flex;
  gap: 8px;
}

.btn-edit {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 6px 10px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  color: #2196F3;
}

.btn-edit:hover {
  background-color: rgba(33, 150, 243, 0.1);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.close-btn:hover {
  color: #333;
}

.modal-form {
  padding: 15px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.form-group {
  margin-bottom: 0;
}

.form-group:nth-child(3) {
  grid-column: 1 / -1;
}

.checkbox-group {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.form-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.form-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.error-text {
  color: #f44336;
  font-size: 11px;
  margin-top: 3px;
  display: block;
}

.help-text {
  color: #999;
  font-size: 11px;
  margin-top: 3px;
  display: block;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  font-weight: normal;
  margin-bottom: 0;
}

.checkbox-group input[type="checkbox"] {
  margin-right: 8px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  background-color: #f9f9f9;
}

.btn-cancel {
  background-color: #e0e0e0;
  color: #333;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background-color 0.3s ease;
}

.btn-cancel:hover {
  background-color: #d0d0d0;
}

.btn-save {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s ease;
}

.btn-save:hover:not(:disabled) {
  background-color: #45a049;
}

.btn-save:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* Vuetify DataTable Customization */
:deep(.users-datatable) {
  font-size: 14px;
}

:deep(.users-datatable .v-table__wrapper) {
  border-radius: 4px;
}

:deep(.users-datatable thead tr) {
  background-color: #f9f9f9 !important;
}

:deep(.users-datatable thead tr th) {
  background-color: #f9f9f9 !important;
  border-color: #ddd !important;
  color: #333 !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #ddd !important;
}

:deep(.users-datatable tbody tr) {
  transition: background-color 0.2s ease;
}

:deep(.users-datatable tbody tr:hover) {
  background-color: #f5f5f5 !important;
}

:deep(.users-datatable tbody tr td) {
  color: #666;
  border-color: #e0e0e0;
  padding: 12px 15px;
}

:deep(.v-pagination) {
  margin-top: 15px;
}

:deep(.v-pagination .v-pagination__item) {
  color: #666;
}

:deep(.v-pagination .v-pagination__item--is-active) {
  background-color: #4CAF50 !important;
  color: white !important;
}

:deep(.v-data-table__td) {
  vertical-align: middle;
}
</style>
